import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { auditService } from '@services/auditService';
import { organizationService } from '@services/organizationService';
import { toDisplayError } from '../utils/apiError';

const WCAG_VERSIONS = ['2.0', '2.1', '2.2'] as const;
const TARGET_LEVELS = ['A', 'AA', 'AAA'] as const;

const auditSchema = z.object({
  name: z.string().max(200, 'audit.initiation.validation.nameTooLong').optional().or(z.literal('')),
  wcag_version: z.enum(WCAG_VERSIONS),
  target_level: z.enum(TARGET_LEVELS),
  description: z
    .string()
    .max(2000, 'audit.initiation.validation.descriptionTooLong')
    .optional()
    .or(z.literal('')),
  testing_environment: z
    .string()
    .max(2000, 'audit.initiation.validation.environmentTooLong')
    .optional()
    .or(z.literal('')),
});

type AuditFormData = z.infer<typeof auditSchema>;

export default function AuditInitiationPage() {
  const { slug, contextId } = useParams<{ slug: string; contextId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [orgId, setOrgId] = useState<string>('');
  const [resolvingOrg, setResolvingOrg] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AuditFormData>({
    resolver: zodResolver(auditSchema),
    defaultValues: {
      name: '',
      wcag_version: '2.2',
      target_level: 'AA',
      description: '',
      testing_environment: '',
    },
  });

  useEffect(() => {
    const resolve = async () => {
      if (!slug) return;
      try {
        // REV-802: through the API client, which carries and renews the
        // in-memory token; a raw fetch would do neither.
        const organization = await organizationService.getBySlug(slug);
        setOrgId(organization.id);
      } catch (err) {
        setServerError(toDisplayError(err, 'Failed to resolve organization'));
      } finally {
        setResolvingOrg(false);
      }
    };
    void resolve();
  }, [slug]);

  const onSubmit = async (data: AuditFormData) => {
    if (!orgId || !contextId) return;
    setServerError(null);
    setIsSubmitting(true);
    try {
      const audit = await auditService.createAudit(orgId, contextId, {
        name: data.name || undefined,
        wcag_version: data.wcag_version,
        target_level: data.target_level,
        description: data.description || undefined,
        testing_environment: data.testing_environment
          ? { notes: data.testing_environment }
          : undefined,
      });
      void navigate(`/orgs/${slug}/audits/${audit.id}`);
    } catch (err: unknown) {
      setServerError(
        toDisplayError(err, t('audit.initiation.errors.createFailed', 'Failed to create audit'))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (resolvingOrg) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
        {t('audit.initiation.title', 'Start a New Audit')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'audit.initiation.subtitle',
          'Create a WCAG conformance audit for this context. You can attest criteria once evidence has been collected.'
        )}
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {serverError}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            {...register('name')}
            label={t('audit.initiation.fields.name', 'Audit name (optional)')}
            placeholder={t('audit.initiation.fields.namePlaceholder', 'e.g. Q2 2026 Audit')}
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.name}
            helperText={errors.name ? t(errors.name.message as string) : undefined}
            disabled={isSubmitting}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Controller
              name="wcag_version"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ minWidth: 180 }} error={!!errors.wcag_version}>
                  <InputLabel id="audit-wcag-version-label">
                    {t('audit.initiation.fields.wcagVersion', 'WCAG Version')}
                  </InputLabel>
                  <Select
                    labelId="audit-wcag-version-label"
                    label={t('audit.initiation.fields.wcagVersion', 'WCAG Version')}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  >
                    {WCAG_VERSIONS.map((v) => (
                      <MenuItem key={v} value={v}>
                        {t('common.wcagVersion', 'WCAG {{version}}', { version: v })}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.wcag_version && (
                    <FormHelperText>{t(errors.wcag_version.message as string)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="target_level"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ minWidth: 180 }} error={!!errors.target_level}>
                  <InputLabel id="audit-target-level-label">
                    {t('audit.initiation.fields.targetLevel', 'Target Level')}
                  </InputLabel>
                  <Select
                    labelId="audit-target-level-label"
                    label={t('audit.initiation.fields.targetLevel', 'Target Level')}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  >
                    {TARGET_LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {t('common.wcagLevel', 'Level {{level}}', { level: l })}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.target_level && (
                    <FormHelperText>{t(errors.target_level.message as string)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          <TextField
            {...register('description')}
            label={t('audit.initiation.fields.description', 'Description (optional)')}
            placeholder={t(
              'audit.initiation.fields.descriptionPlaceholder',
              'Purpose, scope notes, reviewers, dates…'
            )}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            sx={{ mb: 2 }}
            error={!!errors.description}
            helperText={errors.description ? t(errors.description.message as string) : undefined}
            disabled={isSubmitting}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <TextField
            {...register('testing_environment')}
            label={t('audit.initiation.fields.environment', 'Testing environment (optional)')}
            placeholder={t(
              'audit.initiation.fields.environmentPlaceholder',
              'Browser + AT combinations, device notes, viewports…'
            )}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mb: 3 }}
            error={!!errors.testing_environment}
            helperText={
              errors.testing_environment
                ? t(errors.testing_environment.message as string)
                : undefined
            }
            disabled={isSubmitting}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                t('audit.initiation.submit', 'Start Audit')
              )}
            </Button>
            {/* REV-732: Cancel goes to a URL, so when it is available it is a
                LINK. While the form is submitting it navigates nowhere, and by
                the same rule that makes it a button rather than a link with a
                dead href — the shape NavigationDrawer established in REV-679.

                aria-disabled, not the `disabled` attribute: `disabled` drops
                the control out of the tab order, so a keyboard user mid-submit
                loses it entirely rather than finding it and being told it is
                busy. */}
            <Button
              variant="text"
              {...(isSubmitting
                ? { component: 'button' as const, 'aria-disabled': true }
                : {
                    component: RouterLink,
                    to: `/orgs/${slug}/contexts/${contextId}`,
                  })}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
